import React, { useEffect, useRef, useState } from 'react';
import { PageHeader, Table, Input, Select, Space as AntSpace } from 'antd';
import { CloudSyncOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import { handleApiError, MyBreadcrumb } from 'utils';
import {
  yardiSyncCompaniesService,
  yardiSyncFiltersService,
} from 'services/yardi_sync';

const { Search } = Input;

export default function YardiSyncCompanies() {
  const [loading, setLoading] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [items, setItems] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [propertyId, setPropertyId] = useState(undefined);
  const [spaceId, setSpaceId] = useState(undefined);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [spaceOptions, setSpaceOptions] = useState([]);

  const debouncedSetSearch = useRef(
    debounce((value) => {
      setPageNo(1);
      setSearch(value);
    }, 400),
  ).current;

  useEffect(() => {
    return () => debouncedSetSearch.cancel();
  }, [debouncedSetSearch]);

  useEffect(() => {
    setLoadingFilters(true);
    yardiSyncFiltersService({ propertyId: propertyId || undefined })
      .then((res) => {
        setLoadingFilters(false);
        setPropertyOptions(res.data.propertyOptions || []);
        setSpaceOptions(res.data.spaceOptions || []);
      })
      .catch((err) => {
        setLoadingFilters(false);
        handleApiError(err);
        setPropertyOptions([]);
        setSpaceOptions([]);
      });
  }, [propertyId]);

  useEffect(() => {
    setLoading(true);
    yardiSyncCompaniesService({
      pageNo,
      pageSize,
      q: search || undefined,
      propertyId: propertyId || undefined,
      spaceId: spaceId || undefined,
    })
      .then((res) => {
        setLoading(false);
        setItems(res.data.items || []);
        setTotalRows(res.data.totalRows != null ? res.data.totalRows : 0);
      })
      .catch((err) => {
        setLoading(false);
        handleApiError(err);
        setItems([]);
        setTotalRows(0);
      });
  }, [pageNo, pageSize, search, propertyId, spaceId]);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'Id',
      key: 'Id',
      width: 90,
    },
    {
      title: 'Company',
      dataIndex: 'CompanyName',
      key: 'CompanyName',
      sorter: (a, b) =>
        (a.CompanyName || '').localeCompare(b.CompanyName || ''),
    },
    {
      title: 'Code',
      dataIndex: 'CompanyCode',
      key: 'CompanyCode',
      width: 120,
    },
    {
      title: 'Type',
      dataIndex: 'CompanyTypeDescription',
      key: 'CompanyTypeDescription',
      width: 160,
    },
    {
      title: 'Property',
      dataIndex: 'PrimaryPropertyName',
      key: 'PrimaryPropertyName',
    },
    {
      title: 'Status',
      dataIndex: 'RecordStatus',
      key: 'RecordStatus',
      width: 100,
    },
  ];

  return (
    <div>
      <MyBreadcrumb items={[{ label: 'Yardi Sync', path: '' }]} />
      <PageHeader
        ghost={false}
        avatar={{ icon: <CloudSyncOutlined /> }}
        title="Yardi Sync"
        subTitle="Companies from Yardi Kube Public API"
        extra={[
          <AntSpace key="filters" size="middle" wrap>
            <Select
              showSearch
              allowClear
              loading={loadingFilters}
              value={propertyId}
              placeholder="Filter by Property"
              style={{ width: 240 }}
              optionFilterProp="label"
              onChange={(value) => {
                setPageNo(1);
                setPropertyId(value);
                setSpaceId(undefined);
              }}
              options={propertyOptions.map((p) => ({
                value: p.id,
                label: `${p.name}${p.code ? ` (${p.code})` : ''}`,
              }))}
            />
            <Select
              showSearch
              allowClear
              loading={loadingFilters}
              value={spaceId}
              placeholder="Filter by Space Name"
              style={{ width: 260 }}
              optionFilterProp="label"
              onChange={(value) => {
                setPageNo(1);
                setSpaceId(value);
              }}
              options={spaceOptions.map((s) => ({
                value: s.id,
                label: `${s.name} - ${s.propertyName}`,
              }))}
            />
            <Search
              key="search"
              placeholder="Search company, code, property, contact..."
              allowClear
              onSearch={(value) => {
                debouncedSetSearch.cancel();
                setPageNo(1);
                setSearch((value || '').trim());
              }}
              onChange={(e) =>
                debouncedSetSearch((e.target.value || '').trim())
              }
              style={{ width: 320 }}
            />
          </AntSpace>,
        ]}
      />
      <Table
        rowKey={(row) => row.Id}
        loading={loading}
        dataSource={items}
        columns={columns}
        pagination={{
          current: pageNo,
          pageSize,
          total: totalRows,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (t) => `Total ${t} companies`,
          onChange: (p, ps) => {
            setPageNo(p);
            setPageSize(ps);
          },
        }}
      />
    </div>
  );
}
